import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { RaceCard } from "@/components/RaceCard";
import { SportIcon } from "@/components/SportIcon";
import { MONTHS_SELECT } from "@/lib/helpers";
import api from "@/lib/api";
import { Search, SlidersHorizontal, X, Loader2 } from "lucide-react";
import { Drawer, DrawerContent, DrawerTrigger, DrawerClose } from "@/components/ui/drawer";

const SPORTS = [
  { slug: "running", name: "Running" }, { slug: "trail-running", name: "Trail Running" },
  { slug: "cycling", name: "Cycling" }, { slug: "triathlon", name: "Triathlon" },
  { slug: "duathlon", name: "Duathlon" }, { slug: "fitness-racing", name: "Fitness Racing" },
];
const DISTANCES = ["5K", "10K", "15K", "21.1K", "42.2K", "Ultra"];
const DATE_PRESETS = [{ v: "this_weekend", l: "This Weekend" }, { v: "next_weekend", l: "Next Weekend" }, { v: "custom", l: "Custom Date" }];

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#22d3ee]";

function FilterPanel({ f, setF, clear, trackFilter, config }) {
  const Section = ({ title, children }) => (
    <div className="py-4 border-b border-slate-200 last:border-0">
      <h4 className="font-display font-bold uppercase tracking-wide text-sm text-slate-800 mb-3">{title}</h4>{children}
    </div>
  );
  const pick = (key, val) => { const nv = f[key] === val ? "" : val; setF({ ...f, [key]: nv }); trackFilter(key, nv); };
  const chip = (active) => `px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${active ? "bg-[#0a1420] text-white border-[#0a1420]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"}`;
  const countries = config?.countries || ["Malaysia", "Singapore"];
  const states = (config?.states?.[f.country] || config?.states?.Malaysia || []);
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-display font-extrabold uppercase text-lg text-slate-900">Filters</h3>
        <button onClick={clear} className="text-xs text-[#06b6d4] font-semibold hover:underline" data-testid="clear-filters">Clear all</button>
      </div>
      <Section title="Country">
        <select value={f.country} onChange={(e) => { setF({ ...f, country: e.target.value, state: "" }); trackFilter("country", e.target.value); }} data-testid="filter-country" className={inputCls}>
          <option value="">All Countries</option>{countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Section>
      <Section title="State / Location">
        <select value={f.state} onChange={(e) => { setF({ ...f, state: e.target.value }); trackFilter("state", e.target.value); }} data-testid="filter-state" className={inputCls} disabled={states.length === 0}>
          <option value="">{states.length === 0 ? "No sub-regions" : "All States"}</option>{states.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </Section>
      <Section title="Sport">
        <div className="flex flex-col gap-1.5">{SPORTS.map((s) => (
          <button key={s.slug} onClick={() => pick("sport", s.slug)} data-testid={`filter-sport-${s.slug}`}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${f.sport === s.slug ? "bg-cyan-50 text-[#06b6d4]" : "text-slate-600 hover:bg-slate-100"}`}>
            <SportIcon sport={s.slug} size={16} />{s.name}</button>))}</div>
      </Section>
      <Section title="Month">
        <select value={f.month} onChange={(e) => { setF({ ...f, month: e.target.value }); trackFilter("month", e.target.value); }} data-testid="filter-month" className={inputCls}>
          <option value="">All Months</option>{MONTHS_SELECT.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </Section>
      <Section title="Date">
        <div className="flex flex-wrap gap-2">{DATE_PRESETS.map((d) => <button key={d.v} onClick={() => pick("date_filter", d.v)} className={chip(f.date_filter === d.v)} data-testid={`filter-date-${d.v}`}>{d.l}</button>)}</div>
        {f.date_filter === "custom" && (
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div><label className="text-xs text-slate-500">From</label><input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="filter-date-from" className={inputCls} /></div>
            <div><label className="text-xs text-slate-500">To</label><input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="filter-date-to" className={inputCls} /></div>
          </div>
        )}
      </Section>
      <Section title="Distance">
        <div className="flex flex-wrap gap-2">{DISTANCES.map((d) => <button key={d} onClick={() => pick("distance", d)} className={`font-mono-data ${chip(f.distance === d)}`} data-testid={`filter-distance-${d}`}>{d}</button>)}</div>
      </Section>
    </div>
  );
}

const EMPTY_F = { search: "", sport: "", country: "", state: "", distance: "", date_filter: "", month: "", date_from: "", date_to: "" };

export default function Races() {
  const [params, setParams] = useSearchParams();
  const [f, setF] = useState({ ...EMPTY_F, ...Object.fromEntries([...params.entries()].filter(([k]) => k in EMPTY_F)) });
  const [searchInput, setSearchInput] = useState(f.search);
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);

  useEffect(() => { api.get("/config").then(({ data }) => setConfig(data)).catch(() => {}); }, []);

  const trackFilter = (key, val) => { if (val) api.post("/track", { event_type: "filter", metadata: { [key]: val } }).catch(() => {}); };

  const load = useCallback(async () => {
    setLoading(true);
    const qs = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v && !(k === "date_filter" && v === "custom")) qs.set(k, v); });
    if (f.date_filter === "custom") { if (f.date_from) qs.set("date_from", f.date_from); if (f.date_to) qs.set("date_to", f.date_to); }
    setParams(qs, { replace: true });
    try { const { data } = await api.get(`/races?${qs.toString()}`); setRaces(data.races); }
    catch { setRaces([]); } finally { setLoading(false); }
  }, [f, setParams]);

  useEffect(() => { load(); }, [load]);

  const submitSearch = (e) => { e.preventDefault(); setF({ ...f, search: searchInput }); };
  const clear = () => { setF({ ...EMPTY_F }); setSearchInput(""); };
  const activeCount = Object.entries(f).filter(([k, v]) => v && k !== "search").length;

  return (
    <Layout>
      <div className="bg-[#0a1420] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display font-black uppercase text-4xl text-white">Find Your Next Race</h1>
          <form onSubmit={submitSearch} className="mt-4 relative max-w-2xl">
            <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search by race name, city, state or sport..." data-testid="race-search-input"
              className="w-full bg-white rounded-lg pl-12 pr-28 py-3.5 outline-none" />
            <button type="submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-[#22d3ee] text-[#0a1420] font-bold px-5 py-2 rounded-md hover:bg-[#06b6d4]" data-testid="race-search-submit">Search</button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex gap-8">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-24 bg-white border border-slate-200 rounded-xl p-5"><FilterPanel f={f} setF={setF} clear={clear} trackFilter={trackFilter} config={config} /></div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-5">
            <p className="text-slate-600 text-sm" data-testid="results-count">{loading ? "Loading…" : `${races.length} race${races.length !== 1 ? "s" : ""} found`}</p>
            <div className="lg:hidden">
              <Drawer>
                <DrawerTrigger asChild>
                  <button className="inline-flex items-center gap-2 border border-slate-300 rounded-lg px-4 py-2 text-sm font-semibold" data-testid="mobile-filter-open">
                    <SlidersHorizontal size={16} /> Filters {activeCount > 0 && <span className="bg-[#22d3ee] text-[#0a1420] text-xs w-5 h-5 grid place-items-center rounded-full">{activeCount}</span>}
                  </button>
                </DrawerTrigger>
                <DrawerContent className="px-4 pb-6 max-h-[85vh] overflow-y-auto">
                  <div className="flex justify-end pt-2"><DrawerClose asChild><button className="p-2"><X size={20} /></button></DrawerClose></div>
                  <FilterPanel f={f} setF={setF} clear={clear} trackFilter={trackFilter} config={config} />
                  <DrawerClose asChild><button className="w-full mt-4 bg-[#0a1420] text-white font-bold py-3 rounded-lg" data-testid="mobile-filter-apply">Show {races.length} Races</button></DrawerClose>
                </DrawerContent>
              </Drawer>
            </div>
          </div>

          {loading ? (
            <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" size={32} /></div>
          ) : races.length === 0 ? (
            <div className="py-20 text-center">
              <p className="font-display font-bold uppercase text-xl text-slate-700">No races found</p>
              <p className="text-slate-500 mt-2">Try adjusting your filters or search.</p>
              <button onClick={clear} className="mt-4 text-[#06b6d4] font-semibold hover:underline">Clear all filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {races.map((r) => <RaceCard key={r.id} race={r} />)}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
